import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  SpecialistAdminDashboardSliceInitialState,
  APIResponse,
  GetSpecialistAdminConsultationListRequestPayload,
  GetSpecialistAdminConsultationListSuccessResponse,
  GetSpecialistOnboardingListRequestPayload,
  GetSpecialistOnboardingListSuccessResponse,
  GetSpecialistAdminProfileRequestPayload,
  GetSpecialistAdminProfileSuccessResponse,
  SpecialistAdminProfileRequestChangeRequestPayload,
  SpecialistAdminProfileRequestChangeSuccessResponse,
  SetSpecialistAdminProfileFeeAndVisibilityRequestPayload,
  SetSpecialistAdminProfileFeeAndVisibilitySuccessResponse,
  GetHospitalListSuccessResponse,
  SpecialistOnboardingItem,
} from '@/interface';
import { SpecialistProfileStatus } from '@/constants';
import { mergeDeepRight } from 'ramda';

const initialState: SpecialistAdminDashboardSliceInitialState = {
  isLoading: false,
  specialistAdminDashboardSuccess: false,
  specialistAdminDashboardFailure: false,
  dashboard: {
    consultations: [],
    current_page: 0,
    total_pages: 0,
    total_results: 0,
    next_page: null,
  },
  onboarding: {
    specialists: [],
    current_page: 0,
    total_pages: 0,
    total_results: 0,
    next_page: null,
  },
  hospitals: [],
  profile: null,
};

const specialistAdminSlice = createSlice({
  name: 'specialistAdmin',
  initialState,
  reducers: {
    /**
     * get specialist admin consultation list api
     */
    getSpecialistAdminConsultationListRequest: (state, action: PayloadAction<GetSpecialistAdminConsultationListRequestPayload>) => {
      state.isLoading = true;
      state.specialistAdminDashboardSuccess = false;
      state.specialistAdminDashboardFailure = false;
    },
    getSpecialistAdminConsultationListSuccess: (state, action: PayloadAction<GetSpecialistAdminConsultationListSuccessResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardSuccess = action.payload.status_code;
      state.dashboard.consultations = action.payload.data.consultations;
      state.dashboard.current_page = action.payload.data.current_page;
      state.dashboard.total_pages = action.payload.data.total_pages;
      state.dashboard.total_results = action.payload.data.total_results;
      state.dashboard.next_page = action.payload.data.next_page;
    },
    getSpecialistAdminConsultationListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardFailure = action.payload.status_code;
      if (['E-10044', 'E-10054'].includes(action.payload.status_code)) {
        state.dashboard.consultations = [];
        state.dashboard.next_page = null;
        state.dashboard.current_page = 0;
        state.dashboard.total_pages = 0;
        state.dashboard.total_results = 0;
      }
    },

    /**
     * reset messages
     */
    resetSpecialistAdminMessage: state => {
      state.specialistAdminDashboardFailure = false;
      state.specialistAdminDashboardSuccess = false;
    },

    cancelSpecialistAdminRequest: state => {
      state.isLoading = false;
    },

    /**
     * get specialist onboarding list api
     */
    getSpecialistOnboardingListRequest: (state, action: PayloadAction<GetSpecialistOnboardingListRequestPayload>) => {
      state.isLoading = true;
      state.specialistAdminDashboardSuccess = false;
      state.specialistAdminDashboardFailure = false;
    },
    getSpecialistOnboardingListSuccess: (state, action: PayloadAction<GetSpecialistOnboardingListSuccessResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardSuccess = action.payload.status_code;
      state.onboarding.specialists = action.payload.data.specialists;
      state.onboarding.current_page = action.payload.data.current_page;
      state.onboarding.total_pages = action.payload.data.total_pages;
      state.onboarding.total_results = action.payload.data.total_results;
      state.onboarding.next_page = action.payload.data.next_page;
    },
    getSpecialistOnboardingListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardFailure = action.payload.status_code;
      if (['E-10044', 'E-10054'].includes(action.payload.status_code)) {
        state.onboarding.specialists = [];
        state.onboarding.next_page = null;
        state.onboarding.current_page = 0;
        state.onboarding.total_pages = 0;
        state.onboarding.total_results = 0;
      }
    },

    /**
     * get specialist admin profile api
     */
    getSpecialistAdminProfileRequest: (state, action: PayloadAction<GetSpecialistAdminProfileRequestPayload>) => {
      state.isLoading = true;
      state.specialistAdminDashboardSuccess = false;
      state.specialistAdminDashboardFailure = false;
    },
    getSpecialistAdminProfileSuccess: (state, action: PayloadAction<GetSpecialistAdminProfileSuccessResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardSuccess = action.payload.status_code;
      state.profile = action.payload.data;
    },
    getSpecialistAdminProfileFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardFailure = action.payload.status_code;
      state.profile = null;
    },

    /**
     * specialist admin profile request change api
     */
    specialistAdminProfileRequestChangeRequest: (state, action: PayloadAction<SpecialistAdminProfileRequestChangeRequestPayload>) => {
      state.isLoading = true;
      state.specialistAdminDashboardSuccess = false;
      state.specialistAdminDashboardFailure = false;
    },
    specialistAdminProfileRequestChangeSuccess: (state, action: PayloadAction<SpecialistAdminProfileRequestChangeSuccessResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardSuccess = action.payload.status_code;
    },
    specialistAdminProfileRequestChangeFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * set specialist admin profile fee and visibility api
     */
    setSpecialistAdminProfileFeeAndVisibilityRequest: (state, action: PayloadAction<SetSpecialistAdminProfileFeeAndVisibilityRequestPayload>) => {
      state.isLoading = true;
      state.specialistAdminDashboardSuccess = false;
      state.specialistAdminDashboardFailure = false;
    },
    setSpecialistAdminProfileFeeAndVisibilitySuccess: (state, action: PayloadAction<SetSpecialistAdminProfileFeeAndVisibilitySuccessResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardSuccess = action.payload.status_code;
      // Update profile data if it exists
      if (state.profile) {
        state.profile.fee = action.payload.data.fee;
        state.profile.fee_currency = action.payload.data.fee_currency;
        state.profile.is_visible = action.payload.data.is_visible;
        state.profile.profile_status = action.payload.data.profile_status;
      }
    },
    setSpecialistAdminProfileFeeAndVisibilityFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get hospital list api
     */
    getHospitalListRequest: (state) => {
      state.isLoading = true;
      state.specialistAdminDashboardSuccess = false;
      state.specialistAdminDashboardFailure = false;
    },
    getHospitalListSuccess: (state, action: PayloadAction<GetHospitalListSuccessResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardSuccess = action.payload.status_code;
      state.hospitals = action.payload.data.hospitals;
    },
    getHospitalListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.specialistAdminDashboardFailure = action.payload.status_code;
    },

    updateSpecialistOnboardingProfileStatus: (state, action: PayloadAction<{id: number, data: Partial<SpecialistOnboardingItem>}>) => {
      const { id, data } = action.payload;
      const specialists = state.onboarding.specialists;
      const updated = specialists.find(s => s.id === id);
      if (!updated) return;
      const updatedItem = mergeDeepRight(updated, data) as SpecialistOnboardingItem;
      const rest = specialists.filter(s => s.id !== id);
      state.onboarding.specialists = [updatedItem, ...rest];
    }
  },
});

export const {
  // get specialist admin consultation list api
  getSpecialistAdminConsultationListRequest,
  getSpecialistAdminConsultationListSuccess,
  getSpecialistAdminConsultationListFailure,

  // get specialist onboarding list api
  getSpecialistOnboardingListRequest,
  getSpecialistOnboardingListSuccess,
  getSpecialistOnboardingListFailure,

  // get specialist admin profile api
  getSpecialistAdminProfileRequest,
  getSpecialistAdminProfileSuccess,
  getSpecialistAdminProfileFailure,

  // specialist admin profile request change api
  specialistAdminProfileRequestChangeRequest,
  specialistAdminProfileRequestChangeSuccess,
  specialistAdminProfileRequestChangeFailure,

  // set specialist admin profile fee and visibility api
  setSpecialistAdminProfileFeeAndVisibilityRequest,
  setSpecialistAdminProfileFeeAndVisibilitySuccess,
  setSpecialistAdminProfileFeeAndVisibilityFailure,

  // cancel and reset reducers
  resetSpecialistAdminMessage,
  cancelSpecialistAdminRequest,

  // get hospital list api
  getHospitalListRequest,
  getHospitalListSuccess,
  getHospitalListFailure,

  // utility reducers
  updateSpecialistOnboardingProfileStatus,
} = specialistAdminSlice.actions;

export default specialistAdminSlice.reducer;

